import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {BillsRepository} from "../../../../shared/repositories/bills.repository";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatError, MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatOption, provideNativeDateAdapter} from "@angular/material/core";
import {DatePipe} from "@angular/common";
import {
  IBenefitGroup,
  IBill,
  IListBillPackageBenefits,
  IPackageBenefitGroups,
  IPackageShort,
  IRegion,
} from "../../../../shared/models/bills.interface";
import {catchError, forkJoin, map, of, Subject, takeUntil} from "rxjs";
import {MatSelect, MatSelectTrigger} from "@angular/material/select";
import {convertStringToLocalDate} from "../../../../shared/utils/date.format";
import {BillPackageContentComponent} from "../bill-package-content/bill-package-content.component";
import {routesPaths} from "../../../../app.routes";
import {DateInputFormatDirective} from "../../../../shared/directives/date-input-format.directive";

interface IRegionBenefits {
  region: IRegion;
  packageId: string;
  groups: IBenefitGroup[];
}

@Component({
  selector: 'app-bill-form',
  standalone: true,
  imports: [
    MatButton,
    MatIconButton,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelect,
    MatOption,
    MatSelectTrigger,
    BillPackageContentComponent,
    DateInputFormatDirective,
  ],
  templateUrl: './bill-form.component.html',
  styleUrl: './bill-form.component.scss',
  providers: [provideNativeDateAdapter(), DatePipe],
})
export class BillFormComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isEditMode: boolean = false;
  billId: string = '';
  startDateFilter = this.getStartDateFilter.bind(this);
  endDateFilter = this.getEndDateFilter.bind(this);
  packagesList: IPackageShort[] = [];
  regionsList: IRegion[] = [];
  private readonly otherStatesRegionId = '17f96855-3a7b-417b-9c2b-467823550d12';
  benefitGroupsByRegion: IRegionBenefits[] = [];
  isLoadingBenefits: boolean = false;
  showSelectedPackage: boolean = false;
  hasSavedBenefits: boolean = true;
  private preselectedBenefitIds: string[] = [];
  private loadedPackageGroups = new Map<string, IRegionBenefits[]>();
  private currentPackageAltName: string = '';
  destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private billsRepository: BillsRepository,
    private datePipe: DatePipe,
  ) {}

  ngOnInit() {
    this.isEditMode = this.route.snapshot.paramMap.has('id');
    this.billId = this.route.snapshot.paramMap.get('id') ?? '';
    this.buildForm();
    this.loadPackagesList();
    this.loadRegions();

    this.form.get('startDate').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((startDate) => {
      if (startDate) {
        this.form.get('endDate').enable();
      } else {
        this.form.get('endDate').reset();
        this.form.get('endDate').disable();
      }
    });
    this.form.get('selectedPackages').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((altNames: string[]) => {
      const selected = new Set(altNames ?? []);
      for (const key of this.loadedPackageGroups.keys()) {
        if (!selected.has(key)) this.loadedPackageGroups.delete(key);
      }
      const current = this.form.get('selectedPackage').value;
      if (current && !selected.has(current)) {
        this.form.get('selectedPackage').setValue('');
      }
    });
    this.form.get('selectedPackage').valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((altName: string) => {
        if (this.currentPackageAltName && this.benefitGroupsByRegion.length) {
          this.loadedPackageGroups.set(this.currentPackageAltName, [...this.benefitGroupsByRegion]);
        }
        this.currentPackageAltName = altName ?? '';
        if (altName && this.loadedPackageGroups.has(altName)) {
          this.benefitGroupsByRegion = [...this.loadedPackageGroups.get(altName)!];
        } else {
          this.loadPackageBenefits(altName);
        }
      });

    if (this.isEditMode && this.billId) {
      this.loadBill();
    }
  }

  buildForm() {
    this.form = this.fb.group({
      id: [''],
      name: ['', [Validators.required]],
      nPayPeriod: ['', [Validators.required]],
      startDate: ['', [Validators.required]],
      endDate: [{value: '', disabled: true}],
      groupSize: ['', [Validators.required]],
      selectedPackages: [[]],
      selectedPackage: [''],
      loomisNum: [{value: '', disabled: true}],
    });
  }

  loadPackagesList() {
    this.billsRepository.getPackagesList().pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (res?.length) {
        this.packagesList = res;
      }
    });
  }

  getRegionStatesLabel(region: IRegion): string {
    if (region.id.toLowerCase() === this.otherStatesRegionId) return 'other states';
    return region.states?.join(', ') ?? '';
  }

  loadRegions() {
    this.billsRepository.getRegions().pipe(takeUntil(this.destroy$)).subscribe((res) => {
      const regions = res ?? [];
      this.regionsList = regions.sort((a, b) => {
        if (a.id.toLowerCase() === this.otherStatesRegionId) return -1;
        if (b.id.toLowerCase() === this.otherStatesRegionId) return 1;
        return 0;
      });
    });
  }

  loadBill() {
    this.billsRepository.getBill(this.billId).pipe(takeUntil(this.destroy$)).subscribe((bill) => {
      if (bill) {
        this.form.patchValue({
          id: bill.id,
          name: bill.name,
          nPayPeriod: bill.nPayPeriod,
          startDate: convertStringToLocalDate(bill.startDate),
          endDate: convertStringToLocalDate(bill.endDate),
          groupSize: bill.groupSize,
          selectedPackages: bill.selectedPackages?.filter((i) => i.isSelected).map((i: IPackageShort) => i.altName) ?? [],
          loomisNum: bill.loomisNum,
        });
        if (bill.startDate) {
          this.form.get('endDate').enable();
        }
      }
      this.loadSavedBenefits(this.billId);
    });
  }

  loadSavedBenefits(billId: string) {
    this.billsRepository.getBillBenefits(billId).pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.preselectedBenefitIds = (res ?? []).reduce<string[]>(
        (acc, pkg) => acc.concat(pkg.benefitIds ?? []),
        []
      );
      this.hasSavedBenefits = this.preselectedBenefitIds.length > 0;
      if (this.hasSavedBenefits) {
        const packages: string[] = this.form.get('selectedPackages').value ?? [];
        if (packages.length) {
          this.form.get('selectedPackage').setValue(packages[0]);
        }
      }
    });
  }

  loadPackageBenefits(altName: string) {
    if (!altName || !this.regionsList.length) {
      this.benefitGroupsByRegion = [];
      return;
    }
    this.isLoadingBenefits = true;
    const requests = this.regionsList.map(region =>
      this.billsRepository.getBenefitGroups(region.id, altName).pipe(
        catchError(() => of(null)),
        map((result: IPackageBenefitGroups | null) => ({region, result}))
      )
    );
    forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe(regionResults => {
      const loaded = regionResults
        .filter(r => r.result !== null)
        .map(r => {
          this.applyPreselectedBenefits(r.result.groups);
          r.result.groups.sort((a, b) => a.benefitTypeName.localeCompare(b.benefitTypeName));
          return {region: r.region, packageId: r.result.packageId, groups: r.result.groups};
        });
      if (altName) this.loadedPackageGroups.set(altName, loaded);
      if (altName === this.currentPackageAltName) this.benefitGroupsByRegion = loaded;
      this.isLoadingBenefits = false;
    });
  }

  private applyPreselectedBenefits(groups: IBenefitGroup[]) {
    const selectedIds = new Set(this.preselectedBenefitIds);
    groups.forEach((group) => {
      const preselected = group.benefits.find((b) => selectedIds.has(b.id));
      const defaultBenefit = group.benefits.find((b) => b.isDefault);
      group.selectedOption = preselected ?? defaultBenefit ?? group.benefits[0];
    });
  }

  private getLoadedSelectionsForRegion(rb: IRegionBenefits): IListBillPackageBenefits[] {
    if (!rb.packageId || !rb.groups.length) return [];
    const benefitIds = rb.groups
      .filter((g) => g.selectedOption)
      .map((g) => g.selectedOption!.id);
    return [{regionId: rb.region.id, packageId: rb.packageId, benefitIds}];
  }

  applyUniformBenefitSelection(): void {
    const packages: string[] = this.form.get('selectedPackages').value ?? [];
    if (packages.length) {
      this.form.get('selectedPackage').setValue(packages[0]);
    }
    this.showSelectedPackage = true;
  }

  goBack(): void {
    this.router.navigate([routesPaths.billsDashboard]);
  }

  submit() {
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const requestData: IBill = this.generateRequestData();
      this.billsRepository.createBill(requestData).subscribe({
        next: (res: IBill) => {
          this.saveBenefitsThenNavigate(res?.id ?? requestData.id);
        },
        error: (err) => {
          console.log(err);
        }
      });
    }
  }

  private saveBenefitsThenNavigate(billId: string) {
    const action = this.isEditMode ? 'changed' : 'created';
    const selectedAltNames: string[] = this.form.get('selectedPackages').value ?? [];

    // Capture the currently-displayed package before saving
    if (this.currentPackageAltName && this.benefitGroupsByRegion.length) {
      this.loadedPackageGroups.set(this.currentPackageAltName, [...this.benefitGroupsByRegion]);
    }

    const doSave = () => {
      const packages: IListBillPackageBenefits[] = [];
      for (const groups of this.loadedPackageGroups.values()) {
        packages.push(...groups.flatMap(rb => this.getLoadedSelectionsForRegion(rb)));
      }
      if (!packages.length) {
        this.router.navigate([routesPaths.billsDashboard], {state: {success: true, action}});
        return;
      }
      this.billsRepository.saveBillBenefits(billId, packages).subscribe({
        next: () => this.router.navigate([routesPaths.billsDashboard], {state: {success: true, action}}),
        error: () => this.router.navigate([routesPaths.billsDashboard], {state: {success: true, action}}),
      });
    };

    // Load any packages from the Packages dropdown that the user never navigated to
    const unloadedAltNames = selectedAltNames.filter(alt => !this.loadedPackageGroups.has(alt));
    if (!unloadedAltNames.length) {
      doSave();
      return;
    }

    const requests = unloadedAltNames.flatMap(altName =>
      this.regionsList.map(region =>
        this.billsRepository.getBenefitGroups(region.id, altName).pipe(
          catchError(() => of(null)),
          map((result: IPackageBenefitGroups | null) => ({altName, region, result}))
        )
      )
    );
    forkJoin(requests).subscribe(results => {
      const byAltName = new Map<string, IRegionBenefits[]>();
      results
        .filter(r => r.result !== null)
        .forEach(r => {
          this.applyPreselectedBenefits(r.result!.groups);
          if (!byAltName.has(r.altName)) byAltName.set(r.altName, []);
          byAltName.get(r.altName)!.push({region: r.region, packageId: r.result!.packageId, groups: r.result!.groups});
        });
      byAltName.forEach((groups, altName) => this.loadedPackageGroups.set(altName, groups));
      doSave();
    });
  }

  generateRequestData(): IBill {
    const requestData: IBill = {
      name: this.form.value.name,
      nPayPeriod: this.form.value.nPayPeriod,
      startDate: this.datePipe.transform(this.form.value.startDate, 'yyyy-MM-dd'),
      groupSize: this.form.value.groupSize,
      packageAltNames: this.form.value.selectedPackages,
    };
    if (this.form.value.endDate) {
      requestData.endDate = this.datePipe.transform(this.form.value.endDate, 'yyyy-MM-dd');
    }
    if (this.isEditMode) {
      requestData.id = this.form.value.id;
      requestData.loomisNum = this.form.getRawValue().loomisNum;
    }
    return requestData;
  }

  getStartDateFilter(d: Date | null): boolean {
    if (!d) return false;
    return d.getDate() === 1;
  }

  getEndDateFilter(d: Date | null): boolean {
    const startDate = new Date(this.form.get('startDate')?.value);
    if (!startDate || !d) return false;
    return d > startDate;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
